import { type LayoutProps, usePermissions, useTranslate } from 'react-admin'
import {
  AppBar as RaAppBar,
  Box,
  Toolbar,
  Typography,
  styled,
  Drawer,
  IconButton,
  Button
} from '@mui/material'
import React from 'react'
import { isNil } from 'lodash/fp'
import { Menu } from 'components/Menu'
import MenuIcon from '@mui/icons-material/Menu'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { DrawerContext } from 'components/Admin'

const base = import.meta.env.BASE_URL === '/' ? '' : import.meta.env.BASE_URL

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1,
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  position: 'relative'
}))
const RootWithoutTitle = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1,
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  position: 'relative'
}))
const AppFrame = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  overflowX: 'auto'
}))
const Content = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 2,
  //   padding: theme.spacing(10),
  p: 3,
  marginTop: '4em',
  marginLeft: 5
}))
const ContentWithoutTitle = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  //   padding: theme.spacing(10),
  marginTop: '1em',
  marginLeft: '0'
}))
const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end'
}))
const AppBar: React.FC<{
  permissions: string[]
  open: boolean
  setOpen: (open: boolean) => void
}> = ({ permissions, open, setOpen, ...props }) => {
  const translate = useTranslate()
  const toggleMenu = (): void => {
    setOpen(!open)
  }
  return (
    <RaAppBar {...props}>
      <Toolbar>
        <Button
          size="large"
          color="inherit"
          aria-label="menu"
          sx={{ mr: 2 }}
          onClick={toggleMenu}
          endIcon={<ArrowDropDownIcon />}
          startIcon={<MenuIcon />}>
          {translate('menu.title')}
        </Button>
        <Typography variant="h6" id="react-admin-title" />
        <Box flex="1" />
        <svg width="180" height="40">
          <image href={`${base}/siteLogo.svg`} width="180" height="40" />
        </svg>
        {!isNil(process.env['REACT_APP_VERSION']) && (
          <Typography
            variant="subtitle1"
            sx={{ marginTop: '0em', marginLeft: '1em' }}>
            {`${process.env['REACT_APP_VERSION']}`}
          </Typography>
        )}
      </Toolbar>
      <Drawer variant="persistent" anchor="left" open={open}>
        <DrawerHeader>
          <IconButton onClick={toggleMenu}>
            <ChevronLeftIcon />
          </IconButton>
        </DrawerHeader>
        <Menu setOpen={setOpen} permissions={permissions} />
      </Drawer>
    </RaAppBar>
  )
}
export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { permissions, isLoading } = usePermissions()
  if (isLoading) return null
  return (
    <DrawerContext.Consumer>
      {({ open, setOpen }) => (
        <Root>
          <AppFrame>
            <AppBar permissions={permissions} open={open} setOpen={setOpen} />
            <Content>{children}</Content>
          </AppFrame>
        </Root>
      )}
    </DrawerContext.Consumer>
  )
}
export const LayoutWithoutTitle: React.FC<LayoutProps> = ({ children }) => (
  <RootWithoutTitle>
    <AppFrame>
      <ContentWithoutTitle>{children}</ContentWithoutTitle>
    </AppFrame>
  </RootWithoutTitle>
)
